import { useState, useRef, useMemo, useCallback } from 'react';
import useAuth from '../../../hooks/useAuth';
import axiosInstance from '../../../api/axios';
import { Link } from 'react-router-dom';
import { Box, Typography, TextField, Grid, IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff, Login as LoginIcon } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import ReCAPTCHA from "react-google-recaptcha";
import { useTheme } from '@mui/material/styles';
import { notifyError, notifySuccess } from "../../../components/common/toast/ToastFunc";
import LoginImage from "../../../assests/images/login.jpg";
import LoginOTPDialog from '../../../components/common/otp-input/LoginOTPDialog';

const Login = () => {

    const { login } = useAuth();

    const theme = useTheme();

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        user_id: '',
        password: '',
        show_password: false,
    });
    const [otp, setOtp] = useState('');
    const [showLoginOTPDialog, setShowLoginOTPDialog] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);


    const captchaRef = useRef(null);

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const buttonTheme = useMemo(() => ({
        color: 'white',
        backgroundColor: theme.mainColor,
        fontSize: "18px",
        fontWeight: 'bold',
        borderRadius: "7px",
        height: "45px",
        '&:hover': {
            backgroundColor: '#b30000',
            color: 'white',
            fontSize: "18px",
            fontWeight: 'bold',
            borderRadius: "7px",
        },
    }), [theme.mainColor]);

    const textFieldTheme = useMemo(() => ({
        '& label.Mui-focused': {
            color: theme.mainColor,
        },
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: 'grey',
                borderRadius: '7px',
            },
            '&:hover fieldset': {
                borderColor: theme.mainColor,
            },
            '&.Mui-focused fieldset': {
                border: '1px solid #990000',
            },
        },
    }), [theme.mainColor]);

    const verifyOTP = useCallback(async () => {
        try {
            setOtpLoading(true);
            const response = await axiosInstance.post('/auth/verify-login-otp', {
                "user_id": formData["user_id"],
                "otp": otp,
                "otp_source": "Login"
            });
            setShowLoginOTPDialog(false);
            setOtp('');
            login(response.data?.data?.access_token);
        } catch (error) {
            notifyError(error.message)
        } finally {
            setOtpLoading(false);
        }
    }, [formData, login, otp]);

    const handleSubmit = useCallback(async () => {

        try {
            const captcha_token = captchaRef.current.getValue();
            captchaRef.current.reset();

            if (!captcha_token) {
                notifyError("Invalid Captcha");
                return;
            }
            setLoading(true);
            const response = await axiosInstance.post('/auth/login', {
                user_id: formData["user_id"], password: formData["password"], captcha_token: captcha_token
            });
            notifySuccess(response?.data?.message || "Success")
            setShowLoginOTPDialog(true);
        }
        catch (error) {
            notifyError(error.message || "Something went wrong");
        }
        finally {
            setLoading(false);
        }
    }, [formData]);

    return (
        <>
            {showLoginOTPDialog && <LoginOTPDialog showLoginOTPDialog={showLoginOTPDialog} setShowLoginOTPDialog={setShowLoginOTPDialog} otp={otp} setOtp={setOtp} verifyOTP={verifyOTP} otpLoading={otpLoading} />}
            <Grid container spacing={2} sx={{ alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 65px)' }}>
                <Grid item xs={12} md={5}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: "100%",
                            height: "100%",
                            mt: "65px",
                            m: 2
                        }}>
                        <Typography gutterBottom sx={{ fontSize: "35px", color: "#990000", fontWeight: "bold" }}>
                            Hi, Welcome Back
                        </Typography>
                        <Box
                            component="img"
                            sx={{
                                height: 470,
                                width: 470,
                            }}
                            alt="Login Image"
                            src={LoginImage} // Update this path with the actual image path
                        />
                    </Box>
                </Grid>

                <Grid item xs={12} md={7}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                    <Box
                        component="form"
                        noValidate
                        autoComplete="off"
                        sx={{
                            maxWidth: 400,
                            p: 2,
                        }}
                    >
                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 3 }}>
                            <Typography variant="h5" sx={{ color: theme.mainColor, fontWeight: "500" }}>
                                Access your account instantly.
                            </Typography>
                        </Grid>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="User ID"
                                    variant="outlined"
                                    name="user_id"
                                    value={formData.user_id}
                                    onChange={handleInputChange}
                                    sx={{
                                        ...textFieldTheme
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    type={formData.show_password ? 'text' : 'password'}
                                    label="Password"
                                    variant="outlined"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    sx={{
                                        ...textFieldTheme
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => setFormData(prev => ({ ...prev, show_password: !prev.show_password }))}
                                                    onMouseDown={handleMouseDownPassword}
                                                >
                                                    {formData.show_password ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} display="flex" justifyContent="center">
                                <ReCAPTCHA
                                    sitekey={process.env.REACT_APP_SITE_KEY}
                                    ref={captchaRef}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <LoadingButton
                                    onClick={() => handleSubmit()}
                                    loading={loading}
                                    loadingPosition="start"
                                    startIcon={<LoginIcon />}
                                    variant="contained"
                                    sx={{ ...buttonTheme }}
                                    fullWidth
                                >
                                    Login
                                </LoadingButton>
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                <Link to="/forgot-password" style={{ textDecoration: 'none', color: theme.mainColor }}>
                                    <Typography sx={{ fontSize: "18px", fontWeight: "500" }}>
                                        Forgot password?
                                    </Typography>
                                </Link>
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: -1 }}>
                                <Link to="/sign-up" style={{ textDecoration: 'none', color: theme.mainColor }}>
                                    <Typography sx={{ fontSize: "18px", fontWeight: "500" }}>
                                        Not a Customer? Sign Up
                                    </Typography>
                                </Link>
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>
            </Grid>
        </>
    );
};

export default Login;
